import { PdsWidget } from './pds-widget';
import { NavItem } from './interfaces/nav-item';
import { PdsImage } from './interfaces/pds-image';
import { pdsLogger } from './pds-logger';
import { pdsUtils } from './pds-utils';
let log = pdsLogger.prependLogger(pdsLogger.requiresLogger('app-nav'), 'pds-app-nav.ts');

export class PdsAppNav extends PdsWidget {
  is = 'pds-app-nav';

  //Input
  navigation: Array<NavItem> = [];

  //Input
  userNavigation: Array<NavItem>;

  //Input
  dataAppName: string;

  //Input
  user: { firstName: string, lastName: string, profilePicture?: PdsImage};

  // A nav is selected when it is clicked. A nav that is clicked in a primary or
  // secondary menu will be set into it's respective variable.
  selectedNav: NavItem | null;

  //When the selectedNav changes, we store the previous one in here until the setTimeout runs in the
  //setSecondaryNavPosition function.  Then it is cleared out.  This was done to prevent flicker when clicking from one
  //primary nav item to another.
  previouslySelectedNav: NavItem | null;

  //In the mobile nav, the selected NavItem is used to populate the category name and show its children.
  //This variable may contain a secondary or tertiary NavItem.  If we ever expand to doing 5 tier data,
  //it could hold a quaternary NavItem
  mobileMenuSelectedNav: NavItem | null;

  //As the mobileMenuSelectedNav change, so also must this variable change to remember the parent of
  //the mobileMenuSelectedNav.
  mobileMenuSelectedNavParent: NavItem | null;

  //When this is true, we show a secondary menu that supports four tier data instead of the normal
  //one that only supports 3 tier data.
  selectedNavUsesTierFourMenu = false;

  //When the selectedNavUsesTierFourMenu changes, we store the previous one in here until the setTimeout runs in the
  //setSecondaryNavPosition function.  Then it is cleared out.  This was done to prevent flicker when clicking from one
  //primary nav item to another.
  previouslySelectedNavUsesTierFourMenu = false;

  //This is only used when selectedNavUsesTierFourMenu is true.
  //When the mega menu is open this determines whether the third and forth tier
  //panel is showing just a list of items, or grouped list of items.
  //It's true when it's showing a grouped list of items.
  selectedSecondaryNavHasGrandChildren = false;

  // we are using null rather than undefined because Polymer doesn't update the DOM on undefined
  selectedSecondaryNav: NavItem | null;

  // A nav is active when it is part of the route that has been navigated to. A nav that is active
  // will be set into it's respective variable.
  activeNav: NavItem | null;

  //This is only used when selectedNavUsesTierFourMenu is true.
  //Provides the highlight for the active route.
  activeSecondaryNav: NavItem | null;

  // we are using null rather than undefined because Polymer doesn't update the DOM on undefined
  activeTertiaryNav: NavItem | null;

  //only used when selectedNavUsesTierFourMenu is true.
  //Represents the nav item of the current route.
  activeQuaternaryNav: NavItem | null;

  //In hamburger mode, the nav gets opened manually.
  //This is ONLY set to true when in small screen mode.
  //It is always false in wide screen mode.
  isMobileMenuOpen = false;

  //When the user clicks their name, the user menu pops up
  userMenuOpen = false;

  //Shows an indicator that there are nested menu items
  secondaryIndicator = true;

  primaryNavLinkWithChildrenId = '';

  secondaryPositionClass = 'pds-hidden';

  //When the secondaryPositionClass changes, we store the previous one in here until the setTimeout runs in the
  //setSecondaryNavPosition function.  Then it is cleared out.  This was done to prevent flicker when clicking from one
  //primary nav item to another.
  previousSecondaryPositionClass = '';

  thirdAndFourthTierMaxColumns: number;

  //This is set to true ONLY when the navigation variable contains four
  //tier data in it. When it is true, there are some css constraints put on
  //the primary nav container to prevent it from changing width.
  fourTierSupportRequired: boolean | null;

  lastThirdTierNavItemMap: {[id: string]: boolean} = {};

  properties = {
    //data-app-name property can be used to change name next to PowerSchool Logo
    dataAppName: {
      type: String,
      value: 'global|Unified Classroom'
    },
    navigation: {
      observer: 'navigationUpdated'
    },
    fourTierSupportRequired: {
      value: null,
      readOnly: true
    },
    selectedNav: {
      value: null,
      readOnly: true
    },
    mobileMenuSelectedNav: {
      value: null,
      readOnly: true
    },
    mobileMenuSelectedNavParent: {
      value: null,
      readOnly: true
    },
    isMobileMenuOpen: {
      value: null,
      readOnly: true
    },
    thirdAndFourthTierMaxColumns: {
      type: Number,
      value: 3,
      readOnly: true
    },
    previouslySelectedNav: {
      value: null,
      readOnly: true
    }
  };

  // Keeps track of the window width so we can detect when we go from mobile to desktop view
  // or desktop to mobile.
  prevWindowWidth: number;

  // This function closes all the menus when switching view modes from desktop to mobile, and the reverse.
  handleWindowResize: () => void;

  //When clicking a primary item with children, the secondary menu opens up.
  //'hard' open it will be showing on the screen.
  //'closed' means it will not be showing.
  private secondaryOpen = 'closed'; //'closed', 'hard', and 'soft' are the options.

  //polymer life cycle hook. For polymer components, this is essentially the constructor
  created(): void {
    this.handleWindowResize = (): void => {
      let windowWidth = window.innerWidth;
      if (this.prevWindowWidth > 768 && windowWidth <= 768) {
        this.closeAllMenus(null);
      } else if (this.prevWindowWidth <= 768 && windowWidth > 768) {
        this.closeAllMenus(null);
      }
      this.prevWindowWidth = windowWidth;
    };
  }

  attached(): void {
    super.attached();

    setTimeout(() => {
      this.setHighlights();
    });

    this.prevWindowWidth = window.innerWidth;

    window.addEventListener('resize', this.handleWindowResize);
  }

  detached(): void {
    super.detached();
    window.removeEventListener('resize', this.handleWindowResize);
  }

  setSecondaryNavPosition(selectedNavElm: HTMLElement): void {
    // We hide the nav before positioning it so there is no awkward transition animation
    this.previousSecondaryPositionClass = this.secondaryPositionClass;
    this.secondaryPositionClass = 'pds-is-invisible';
    this.setSecondaryOpenState('hard');

    if (this.isMobileMenuOpen) {
      return;
    }

    setTimeout(() => {
      let secondaryNav: HTMLElement | null = this.querySelector('.pds-nav-secondary-selected');
      let primaryNav: HTMLElement | null = this.querySelector('.pds-nav-primary');
      let primaryNavList: HTMLElement | null = this.querySelector('.pds-primary-nav-list');

      let appHeadHeight = 0;

      if (this.offsetTop != null) {
        appHeadHeight = this.offsetTop;
      }

      // This is the amount of space available for the secondary nav to fit
      let availableHeight = window.innerHeight - appHeadHeight;

      this.setPreviouslySelectedNav(null);
      this.previousSecondaryPositionClass = '';

      if (this.selectedNavUsesTierFourMenu) {
        let fourTierNavWindow = this.querySelector('.pds-app-nav-mega-menu-selected');
        fourTierNavWindow.style.top = appHeadHeight + 'px';
        fourTierNavWindow.style.height = `calc(100vh - ${appHeadHeight}px)`;
        this.secondaryPositionClass =  '';
      } else if (secondaryNav != null && primaryNav != null && primaryNavList != null) {
        const primaryNavItemHeight = selectedNavElm.clientHeight;
        // Grabbing the width from the computed style returns it as a decimal instead of rounding it to the nearest whole number
        let primaryNavStyles: CSSStyleDeclaration = getComputedStyle(primaryNav);
        let primaryNavWidth = 0;

        if (primaryNavStyles.width != null && primaryNavStyles.borderRightWidth != null) {
          primaryNavWidth = parseFloat(primaryNavStyles.width);
        }

        // Design wants a 10px margin from the window's edge for
        // secondary navs that fit on screen, but go off screen when centered
        const collisionExtraMargin = 10;

        // 0 = first primary navItem, 1 = second primary navItem, etc.
        let selectedNavNumber: number = Math.ceil((selectedNavElm.getBoundingClientRect().top - primaryNav.getBoundingClientRect().top + primaryNavList.scrollTop) / primaryNavItemHeight);

        // y-position of selectedNav's center (0 = top of the nav)
        let centerOfNavClicked = (selectedNavNumber * primaryNavItemHeight) + (primaryNavItemHeight / 2) - primaryNavList.scrollTop;

        let secondaryNavHeight = secondaryNav.clientHeight;

        // Mobile view
        if (window.innerWidth < 769) {
          secondaryNav.style.top = appHeadHeight + 'px';

          // This activates the change detection and removes the pds-is-insvisible class
          this.secondaryPositionClass = '';
          pdsUtils.focus('.pds-nav-category > a', this as any as HTMLElement);

        // Cannot fit in the container
        } else if (secondaryNavHeight > availableHeight - collisionExtraMargin) {
          secondaryNav.style.left = primaryNavWidth + 'px';
          secondaryNav.style.top = appHeadHeight + 'px';
          secondaryNav.style.height = 'calc(100% - ' + appHeadHeight + 'px)';

          // This activates the change detection and removes the pds-is-insvisible class
          this.secondaryPositionClass = '';

        // Collides with top of the container
        } else if ((secondaryNavHeight / 2) > centerOfNavClicked) {
          secondaryNav.style.left = primaryNavWidth + 'px';
          secondaryNav.style.top = appHeadHeight + 'px';

          // This activates the change detection and removes the pds-is-insvisible class
          this.secondaryPositionClass =  '';

        // Collides with the bottom of the container
        } else if (centerOfNavClicked + (secondaryNavHeight / 2) > availableHeight) {
          secondaryNav.style.left = primaryNavWidth + 'px';

          // This activates the change detection and replaces the pds-is-insvisible class
          this.secondaryPositionClass = 'pds-secondary-bottom';

        // Fits and can be centered in the container
        } else {
          secondaryNav.style.left = primaryNavWidth + 'px';
          secondaryNav.style.top = (centerOfNavClicked - (secondaryNavHeight / 2) + appHeadHeight) + 'px';

          // This activates the change detection and removes the pds-is-insvisible class
          this.secondaryPositionClass = '';
        }

        let overlay = this.querySelector('.pds-nav-overlay');
        if (overlay != null) {
          overlay.style.width = 'calc(100vw - ' + primaryNavWidth + 'px)';
        }
      }
    });
  }

  primaryClickHandler(event: Event): void {
    log('primaryClickHandler called');
    event.preventDefault();

    let target = <HTMLElement & { dataNav: NavItem, dataIndex: string }>event.target;
    if (!pdsUtils.is(target, 'a')) {
      target = <HTMLElement & { dataNav: NavItem, dataIndex: string }>pdsUtils.getClosest(target, 'a');
    }

    if (target == null) {
      target = <HTMLElement & { dataNav: NavItem, dataIndex: string }>event.srcElement;
    }

    let nav: NavItem = target.dataNav;
    if (this.selectedNav != null) {
      if (this.selectedNav.id === nav.id && this.secondaryOpen !== 'closed') {
        this.closeSecondary(event);
        return;
      }
    }

    this.previouslySelectedNavUsesTierFourMenu = this.selectedNavUsesTierFourMenu;
    this.selectedNavUsesTierFourMenu = this.isTierNumber(nav, 4);
    if (this.selectedNavUsesTierFourMenu && nav.children != null) {
      //Gotta set the selected secondary nav as well here.
      //if the secondary active is a child of nav, then we use that one, otherwise we just use the first child
      if (nav.children.find((secondaryNavItem: NavItem) => secondaryNavItem === this.activeSecondaryNav)) {
        this.selectedSecondaryNav = this.activeSecondaryNav;
      } else {
        this.selectedSecondaryNav = nav.children[0];
      }

      if (this.selectedSecondaryNav != null) {
        this.selectedSecondaryNavHasGrandChildren = this.isTierNumber(this.selectedSecondaryNav, 3);
      }

      this.updateRequiredColumns();
    }

    this.setPreviouslySelectedNav(this.selectedNav);
    this.setSelectedNav(nav);

    if (this.isMobileMenuOpen) {
      this.setMobileMenuSelectedNav(nav);
      this.focusOnCloseMobileMenuButton();
    }

    if (this.selectedNav == null) {
      //really, we just set it so this is really just a bit of code to turn off
      //compiler errors
      return;
    }

    if (nav.children != null && nav.children.length > 0) {
      this.setSecondaryNavPosition(target);
      //This is how you tell polymer that something in an array changed.
      //this.notifyPath(`navigation.${target.dataIndex}.isActive`);
    } else {
      this.closeMobileMenu();
      this.setSecondaryOpenState('closed');
      if (this.selectedNav.onUserClick != null) {
        // Active navs are the navs used in navigation. We navigate here, so we set the active navs
        this.activeNav = this.selectedNav;
        this.activeSecondaryNav = null;
        this.activeTertiaryNav = null;
        this.selectedNav.onUserClick(this.selectedNav, event);
      } else {
        throw 'NavItems that do not have children must have the onUserClick function';
      }
      this.setSelectedNav(null);
    }
  }

  /**
   * Sets the selectedNav variable since this is set to a readonly variable in Polymer
   */
  setSelectedNav(navItem: NavItem | null): void {
    this.setPrivatePolymerPropertyValue('selectedNav', navItem);
  }

  /**
   * Sets the mobileMenuSelectedNav variable since this is set to a readonly variable in Polymer
   */
  setMobileMenuSelectedNav(navItem: NavItem | null): void {
    this.setPrivatePolymerPropertyValue('mobileMenuSelectedNav', navItem);
  }

  /**
   * Sets the mobileMenuSelectedNavParent variable since this is set to a readonly variable in Polymer
   */
  setMobileMenuSelectedNavParent(navItem: NavItem | null): void {
    this.setPrivatePolymerPropertyValue('mobileMenuSelectedNavParent', navItem);
  }

  /**
   * Sets the isMobileMenuOpen variable since this is set to a readonly variable in Polymer
   */
  setMobileMenuOpen(value: boolean): void {
    this.setPrivatePolymerPropertyValue('isMobileMenuOpen', value);
  }

  /**
   * Sets the thirdAndFourthTierMaxColumns variable since this is set to a readonly variable in Polymer
   */
  setThirdAndFourthTierMaxColumns(value: number): void {
    this.setPrivatePolymerPropertyValue('thirdAndFourthTierMaxColumns', value);
  }

  /**
   * Sets the previouslySelectedNav variable since this is set to a readonly variable in Polymer
   */
  setPreviouslySelectedNav(value: NavItem | null): void {
    this.setPrivatePolymerPropertyValue('previouslySelectedNav', value);
  }

  secondaryClickHandler(event: Event): void {
    log('secondaryClickHandler called');
    event.preventDefault();

    let target = <HTMLElement & { dataNav: NavItem, dataParent: NavItem }>event.target;
    if (!pdsUtils.is(target, 'a')) {
      target = <HTMLElement & { dataNav: NavItem, dataParent: NavItem }>pdsUtils.getClosest(target, 'a');
    }

    let nav: NavItem = target.dataNav;
    let parent: NavItem = target.dataParent;

    this.selectedSecondaryNav = nav;

    // if (this.selectedNavUsesTierFourMenu) {
    //   this.selectedSecondaryNavHasGrandChildren = this.isTierNumber(this.selectedSecondaryNav, 3);
    //   this.updateRequiredColumns();
    // }

    if (this.selectedSecondaryNav.onUserClick != null) {
      // Active navs are the navs used in navigation. We navigate here, so we set the active navs
      this.activeNav = this.selectedNav;
      this.activeSecondaryNav = this.selectedSecondaryNav;
      this.activeTertiaryNav = null;
      this.selectedSecondaryNav.onUserClick(this.selectedSecondaryNav, event);
    } else {
      throw `NavItem ${nav.id} did not have onUserClick function defined.`;
    }
    this.setSelectedNav(null);
    this.setSecondaryOpenState('closed');
    this.closeMobileMenu();
  }

  tertiaryClickHandler(event: Event): void {
    event.preventDefault();

    let target = <HTMLElement & { dataNav: NavItem, dataParent: NavItem }>event.target;
    if (!pdsUtils.is(target, 'a')) {
      target = <HTMLElement & { dataNav: NavItem, dataParent: NavItem }>pdsUtils.getClosest(target, 'a');
    }

    let parent: NavItem = target.dataParent;
    let selectedTertiaryNav: NavItem = target.dataNav;

    // If the tertiary menu is open the grandparent is incorrectly set into the parent variable
    // However, the selectedSecondaryNav is already correct in this case
    // So as long as we didn't accidentally grab the grandparent, we set the selected secondary nav
    if (this.selectedNav !== null && this.selectedNav.id !== parent.id) {
      this.selectedSecondaryNav = parent;
    }

    if (selectedTertiaryNav.onUserClick != null) {
      // Active navs are the navs used in navigation. We navigate here, so we set the active navs
      this.activeNav = this.selectedNav;
      this.activeSecondaryNav = this.selectedSecondaryNav;
      this.activeTertiaryNav = selectedTertiaryNav;
      this.activeQuaternaryNav = null;  //If we clicked a third tier, then the fourth tier won't be active.
      selectedTertiaryNav.onUserClick(selectedTertiaryNav, event);
    } else {
      throw `NavItem ${selectedTertiaryNav.id} did not have onUserClick function defined.`;
    }
    this.setSelectedNav(null);
    this.setSecondaryOpenState('closed');
    this.closeMobileMenu();
  }

  secondaryTierClickHandler(event: Event): void {
    log('secondaryTierClickHandler called');
    event.preventDefault();

    let target = <HTMLElement & { dataNav: NavItem, dataParent: NavItem }>event.target;
    if (!pdsUtils.is(target, 'a')) {
      target = <HTMLElement & { dataNav: NavItem, dataParent: NavItem }>pdsUtils.getClosest(target, 'a');
    }

    let primaryNavItem: NavItem = target.dataParent;
    let secondaryNavItem: NavItem = target.dataNav;

    this.selectedSecondaryNav = secondaryNavItem;
    if (this.selectedNavUsesTierFourMenu) {
      this.selectedSecondaryNavHasGrandChildren = this.isTierNumber(this.selectedSecondaryNav, 3);
      this.updateRequiredColumns();
    }

    setTimeout(() => {
      setTimeout(() => {
        pdsUtils.focus('.pds-app-nav-third-and-fourth-tiers a', this as any as HTMLElement);
      });
    });
  }

  // hasForthTier(navItem: NavItem): boolean {
  //   let hasChildren: boolean = navItem.children != null && navItem.children.length > 0;
  //   console.log(navItem.name + ' has children: ' + hasChildren);
  //   return hasChildren;
  // }

  quaternaryClickHandler(event: Event): void {
    log('quaternaryClickHandler called');
    event.preventDefault();

    let target = <HTMLElement & { dataNav: NavItem, dataParent: NavItem }>event.target;
    if (!pdsUtils.is(target, 'a')) {
      target = <HTMLElement & { dataNav: NavItem, dataParent: NavItem }>pdsUtils.getClosest(target, 'a');
    }

    let tertiaryNavItem: NavItem = target.dataParent;
    let quaternaryNavItem: NavItem = target.dataNav;

    if (quaternaryNavItem.onUserClick != null) {
      // Active navs are the navs used in navigation. We navigate here, so we set the active navs
      this.activeNav = this.selectedNav;
      this.activeSecondaryNav = this.selectedSecondaryNav;
      this.activeTertiaryNav = null;
      this.activeQuaternaryNav = quaternaryNavItem;
      quaternaryNavItem.onUserClick(quaternaryNavItem, event);
    } else {
      throw `NavItem ${quaternaryNavItem.id} did not have onUserClick function defined.`;
    }

    this.setSelectedNav(null);
    this.setSecondaryOpenState('closed');
    this.closeMobileMenu();
  }

  /**
   * Click handler for NavItems that handle clicks from the showing secondary, tertiary, and quaternary nav items.
   * This will either route to a page or show that item's children in the nav menu.
   * @param event
   */
  mobileMenuNavClickHandler(event: Event): void {
    event.preventDefault();

    let target = <HTMLElement & { dataNav: NavItem, dataParent: NavItem }>event.target;
    if (!pdsUtils.is(target, 'a')) {
      target = <HTMLElement & { dataNav: NavItem, dataParent: NavItem }>pdsUtils.getClosest(target, 'a');
    }

    let parentNavItem: NavItem = target.dataParent;
    let navItem: NavItem = target.dataNav;

    if (navItem.children != null && navItem.children.length > 0) {
      //It has children!  Let's set it to the selected nav.
      this.setMobileMenuSelectedNav(navItem);
      this.setMobileMenuSelectedNavParent(parentNavItem);
    } else {
      if (navItem.onUserClick != null) {
        // Active navs are the navs used in navigation. We navigate here, so we set the active navs
        this.setActiveStates(...this.getAncestorChain(navItem));

        //clear out the mobile selected variables.
        this.setMobileMenuSelectedNavParent(null);
        this.setMobileMenuSelectedNav(null);
        navItem.onUserClick(navItem, event);
      } else {
        throw `NavItem ${navItem.id} did not have onUserClick function defined.`;
      }

      this.setSelectedNav(null);
      this.setSecondaryOpenState('closed');
      this.closeMobileMenu();
    }

    this.focusOnCloseMobileMenuButton();
    this.scrollMobilePanelToTop();
  }

  scrollMobilePanelToTop(): void {
    let mobileMenuSelectedChildren: HTMLElement | null = this.querySelector('.pds-app-nav-selected-children');
    if (mobileMenuSelectedChildren != null) {
      mobileMenuSelectedChildren.scrollTop = 0;
    }
  }

  /**
   * Sets the active NavItem variables to primary, secondary, tertiary, and quaternary.
   * Any item set to undefined will clear the variable it would be assigned to.
   */
  setActiveStates(primary?: NavItem, secondary?: NavItem, tertiary?: NavItem, quaternary?: NavItem): void {
    this.activeNav = primary || null;
    this.activeSecondaryNav = secondary || null;
    this.activeTertiaryNav = tertiary || null;
    this.activeQuaternaryNav = quaternary || null;
  }

  /**
   * Gets the ancestors of the passed in NavItem INCLUDING the nav item passed in.
   * This function was created to pump the output into the setActiveStates() function.
   * If you have A -> B -> C -> D and you pass in C to this function, it will return [A, B, C].
   */
  getAncestorChain(navItem: NavItem): Array<NavItem> {
    let chain: Array<NavItem> = [navItem];
    let parent: NavItem | null = this.getParent(navItem);
    while (parent != null) {
      chain.unshift(parent);
      parent = this.getParent(parent);
    }

    return chain;
  }

  /**
   * Handles the go back to the previous menu functionality.
   */
  mobileMenuGoBackClickHandler(event: Event): void {
    event.preventDefault();

    if (this.mobileMenuSelectedNavParent == null) {
      //When the mobileMenuSelectedNav is inside the this.navigation array,
      //then this.mobileMenuSelectedNavParent would be null because those items don't have parents.
      //In that case, it means we need to go back to the MainMenu that shows off the items in this.navigation

      this.setSelectedNav(null);
      this.setMobileMenuSelectedNavParent(null);
      this.setMobileMenuSelectedNav(null);
    } else {
      //Easy here. Just set the variables to their parents and we will have gone up 1 level.
      this.setMobileMenuSelectedNav(this.mobileMenuSelectedNavParent);
      this.setMobileMenuSelectedNavParent(this.getParent(this.mobileMenuSelectedNavParent));
    }

    this.focusOnCloseMobileMenuButton();
    this.scrollMobilePanelToTop();
  }

  focusOnCloseMobileMenuButton(): void {
    log('focusOnCloseMobileMenuButton called');
    //Yes, I know that nested setTimouts are ugly. When it comes to focusing on things, we have found that this is the
    //only way that works.
    setTimeout(() => {
      setTimeout(() => {
        pdsUtils.focus('#nav-primary-close-link', this as any as HTMLElement);
      });
    });
  }

  /**
   * Gets the parent of the NavItem being passed in.
   * @param possibleParents - Defaults to this.navigation.
   * This is generally used only for the recursive calls to this function.
   */
  getParent(childNavItem: NavItem, possibleParents?: Array<NavItem>): NavItem | null {
    if (possibleParents == null) {
      possibleParents = this.navigation;
    }
    let parent: NavItem | null = null;
    let isParent = function(n: NavItem): boolean {
      if (n.children == null) {
        return false;
      }
      return null != n.children.find((c: NavItem) => c === childNavItem);
    };

    for (let nav of possibleParents) {

      if (isParent(nav)) {
        return nav;
      } else {
        if (nav.children != null) {
          parent = this.getParent(childNavItem, nav.children);
          if (parent != null) {
            return parent;
          }
        }
      }
    }
    return null;
  }

  showPrimaryNavLinks(): boolean {
    return !this.isMobileMenuOpen || this.mobileMenuSelectedNav == null;
  }

  isNavItemActive(navItem: NavItem): 'pds-is-active' | '' {

    if (navItem === this.activeNav ||
        navItem === this.activeSecondaryNav ||
        navItem === this.activeTertiaryNav ||
        navItem === this.activeQuaternaryNav) {
      return 'pds-is-active';
    }
    return '';
  }

  userMenuClickHandler(event: Event): void {
    event.preventDefault();

    let target = <HTMLElement & { dataNav: NavItem }>event.target;
    if (!pdsUtils.is(target, 'a')) {
      target = <HTMLElement & { dataNav: NavItem }>pdsUtils.getClosest(target, 'a');
    }

    let nav = target.dataNav;
    this.toggleUserMenu();
    this.closeMobileMenu();

    if (nav.onUserClick != null) {
      nav.onUserClick(nav, event);
    } else {
      throw `NavItem ${nav.id} did not have onUserClick function defined.`;
    }
  }

  closeSecondary(event: Event | null): void {
    if (event != null) {
      event.preventDefault();
    }
    this.setSecondaryOpenState('closed');
    if (this.selectedNav != null) {
      /**
       * When the secondary nav closes, we want focus to switch back to the primary nav that got clicked
       */
      pdsUtils.focus('#' + this.selectedNav.id, this as any as HTMLElement);
    }
    this.setSelectedNav(null);
  }

  // Closes all open nav menus
  closeAllMenus(event: Event | null): void {
    if (event != null) {
      event.preventDefault();
    }

    if (this.secondaryOpen !== 'closed') {
      this.closeSecondary(event);
    }
    if (this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }

    this.setMobileMenuSelectedNavParent(null);
    this.setMobileMenuSelectedNav(null);
    this.lastThirdTierNavItemMap = {};
  }

  //Hamburger menu triggers a class state change on the component level for styling
  toggleNav(event: Event): void {
    event.preventDefault();
    this.setMobileMenuOpen(!this.isMobileMenuOpen);
    if (this.isMobileMenuOpen) {
      //These are supposed to be clear when the nav first opens in mobile mode.
      //We are clearing them out here just in case something else forgot to clear them.
      this.setMobileMenuSelectedNavParent(null);
      this.setMobileMenuSelectedNav(null);
    }
    this.focusOnCloseMobileMenuButton();
    this.scrollMobilePanelToTop();
  }

  closeMobileMenu(): void {
    this.setMobileMenuOpen(false);
  }

  toggleUserMenu(event?: Event): void {
    if (event != null) {
      event.preventDefault();
    }

    // Only open the menu if the length is greater than 0, otherwise there will be a blank div.
    if (this.userNavigation.length > 0) {
      this.userMenuOpen = !this.userMenuOpen;
    }
  }

  hasSecondaryItem(currentNav): string | undefined {
    if (currentNav.children != null && currentNav.children.length > 0 && this.secondaryIndicator) {
      return 'pds-has-secondary-item';
    }
  }
  isUsernameShort(): boolean {
    return (this.user.firstName.length + this.user.lastName.length <= 15) ? true : false;
  }

  getFirstName(): string {
    return (this.user.firstName.length > 15) ? (this.user.firstName.slice(0, 15) + '...') : this.user.firstName;
  }

  getLastName(): string {
    return (this.user.lastName.length > 15) ? (this.user.lastName.slice(0, 15) + '...') : this.user.lastName;
  }

  getUserInitialsForDisplay(): string {
    let userInitials = '';
    userInitials = this.user.firstName.substring(0, 1) + this.user.lastName.substring(0, 1);
    return userInitials;
  }

  hasChildren(item: NavItem): boolean {
    return item.children != null && item.children.length > 0;
  }

  hasUserProfilePicture(): boolean {
    return this.user != null && this.user.profilePicture != null;
  }

  clearCurrentState(): void {
    this.activeNav = null;
    this.activeSecondaryNav = null;
    this.activeTertiaryNav = null;
    this.activeQuaternaryNav = null;
    this.setSelectedNav(null);
  }

  navigationUpdated(): void {
    log('navigationUpdated() called');
    this.setPrivatePolymerPropertyValue('fourTierSupportRequired', false);

    if (this.navigation != null) {
      this.setPrivatePolymerPropertyValue('fourTierSupportRequired', this.navigation.find((navItem: NavItem) => {
        return this.isTierNumber(navItem, 4);
      }) != null);
    }
  }

  /**
   * Returns true when the normal secondary nav should be open.
   *
   * NOTE: There will be a brief period of time when this will allow two secondary navs to show up at once.
   * The idea is that the previous menu needs to stay up until the newly created menu gets positioned correctly.
   * Once the new one is positioned correctly, we turn the previous menu off.
   */
  isShowingSecondaryMenu(nav: NavItem, selectedNav: NavItem): boolean {
    if (nav === this.previouslySelectedNav && !this.previouslySelectedNavUsesTierFourMenu && !this.isMobileMenuOpen) {
      return true;
    }

    //First do the easy checks.
    if (nav == null || selectedNav == null || nav !== selectedNav) {
      return false;
    }

    return !this.selectedNavUsesTierFourMenu && !this.isMobileMenuOpen;
  }

  /**
   * Called from the template to determine whether or not to show the secondary menu with
   * four tier data support.
   * Returns true when it should show, false otherwise.
   *
   * NOTE: There will be a brief period of time when this will allow two four tier menues to show up at once.
   * The idea is that the previous menu needs to stay up until the newly created menu gets positioned correctly.
   * Once the new one is positioned correctly, we turn the previous menu off.
   */
  isShowingFourTierMenu(nav: NavItem, selectedNav: NavItem): boolean {
    if (nav === this.previouslySelectedNav && this.previouslySelectedNavUsesTierFourMenu && !this.isMobileMenuOpen) {
      return true;
    }

    //First do the easy checks.
    if (nav == null || selectedNav == null || nav !== selectedNav) {
      return false;
    }

    return this.selectedNavUsesTierFourMenu && !this.isMobileMenuOpen;
  }

  /**
   * When this is true, it turns on the 2nd, 3rd, and 4th tier menu template.
   */
  isShowingMobileMenu(nav: NavItem): boolean {
    if (nav == null || this.selectedNav == null || nav !== this.selectedNav) {
      return false;
    }

    return this.isMobileMenuOpen;
  }

  /**
   * Spiders through the NavItem tree just long enough to verify that the navItem is Tier number.
   * The point was to ask, is this navItem tier 4?  Yes, when it is a great grandparent, No, if it is not a great
   * grandparent.
   */
  isTierNumber(navItem: NavItem, tierNumber: number): boolean {
    if (tierNumber === 1) {
      if (navItem.children == null || navItem.children.length === 0) {
        return true;
      }
      return false;
    }

    if (tierNumber > 1) {
      if (navItem.children == null || navItem.children.length === 0) {
        return false;
      }

      return null != navItem.children.find((nItem: NavItem) => {
        return this.isTierNumber(nItem, tierNumber - 1);
      });
    }

    console.error('the isTierNumber function should have a tierNumber and it should be 1 or more.)');
    return false;
  }

  isLastItemInGroup(navItem: NavItem): string {
    return this.lastThirdTierNavItemMap[navItem.id] ? 'pds-app-nav-tertiary-item-last' : '';
  }

  updateRequiredColumns(): void {
    //Set it back to defaults.
    this.lastThirdTierNavItemMap = {};
    this.setThirdAndFourthTierMaxColumns(3);

    if (this.selectedSecondaryNav == null || this.selectedSecondaryNav.children == null || this.selectedSecondaryNav.children.length === 0) {
      return;
    }

    let itemGroups: Array<NavItem> = [];
    let groupOfSingleItems: NavItem = {name: 'group of single items. Never should show up in the UI', id: ''};
    groupOfSingleItems.children = [];
    for (let child of this.selectedSecondaryNav.children) {
      if (child.children != null && child.children.length > 0) {
        //Has children, so it's already a group.
        itemGroups.push(child);
        if (groupOfSingleItems.children.length > 0) {
          let lastItem = groupOfSingleItems.children[groupOfSingleItems.children.length - 1];
          this.lastThirdTierNavItemMap[lastItem.id] = true;
        }
        groupOfSingleItems.children = [];
      } else {
        //No children, so it needs to be added to the fake group.
        groupOfSingleItems.children.push(child);
        if (groupOfSingleItems.children.length === 1) {
          itemGroups.push(groupOfSingleItems);
        }
      }
    }

    switch (itemGroups.length) {
      case 1:
        this.setThirdAndFourthTierMaxColumns(1);
        break;
      case 2:
        this.setThirdAndFourthTierMaxColumns(2);
        break;
      default:
        this.setThirdAndFourthTierMaxColumns(3);
    }
  }

  private setSecondaryOpenState(secondaryOpen: string): void {
    this.secondaryOpen = secondaryOpen;
  }

  private setHighlights(secondaryState = 'closed'): void {
    log('setHighlights called');
    this.activeNav = null;
    this.activeSecondaryNav = null;
    this.activeTertiaryNav = null;
    this.activeQuaternaryNav = null;

    if (this.navigation != null) {
      (<any>this.navigation).find((navItem: NavItem) => {
        // When there are children, search through them to see if one is active, if so, highlight the parent too.
        if (navItem.children != null && navItem.children.length > 0) {
          return null != (<any>navItem.children).find((childItem: NavItem) => {
            //When there are grandchildren, search through them to see if one is active, if so, highlight the parent and grandparent too.
            if (childItem.children != null && childItem.children.length > 0) {
              (<any>childItem.children).find((grandchildItem: NavItem) => {
                if (grandchildItem.children != null && grandchildItem.children.length > 0) {
                  grandchildItem.children.find((greatGrandChildItem: NavItem) => {
                    if (greatGrandChildItem.isCurrentRoute != null && greatGrandChildItem.isCurrentRoute(greatGrandChildItem)) {
                      this.activeQuaternaryNav = greatGrandChildItem;
                      this.activeTertiaryNav = grandchildItem;
                      this.activeSecondaryNav = childItem;
                      this.activeNav = navItem;
                      return true;
                    }
                    return false;
                  });
                } else {
                  if (grandchildItem.isCurrentRoute != null && grandchildItem.isCurrentRoute(grandchildItem)) {
                    this.activeTertiaryNav = grandchildItem;
                    this.activeSecondaryNav = childItem;
                    this.activeNav = navItem;
                    this.setSecondaryOpenState(secondaryState);
                    return true;
                  }
                  return false;
                }
              });
            } else {
              if (childItem.isCurrentRoute != null && childItem.isCurrentRoute(childItem)) {
                this.activeSecondaryNav = childItem;
                this.activeNav = navItem;
                this.setSecondaryOpenState(secondaryState);
                return true;
              }
              return false;
            }

            //No active children in the current childItem.children array
            return false;
          });
        }

        //If no children, then see if the item is active
        if (navItem.isCurrentRoute != null && navItem.isCurrentRoute(navItem)) {
          this.activeNav = navItem;
          return true;
        }

        //Nothing found, move on to the next one.
        return false;
      });
    }
  }
}
